import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ForumIcon from '@mui/icons-material/Forum';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useContactmomentenVan } from '../../api/contactmomenten.ts';
import { useBedrijf, useBedrijfMutaties, useContactpersoonMutaties } from '../../api/klanten.ts';
import type { BedrijfDetails, Contactpersoon } from '../../api/typen.ts';
import { useVacatures } from '../../api/vacatures.ts';
import { useSessie } from '../../auth/SessieContext.tsx';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { ContactmomentDialoog } from '../../componenten/ContactmomentDialoog.tsx';
import { ContactmomentenLijst } from '../../componenten/ContactmomentenLijst.tsx';
import { Kenmerken } from '../../componenten/Kenmerken.tsx';
import { locatieLabel } from '../../componenten/keuzevelden.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { datum, ofStreepje, volledigeNaam } from '../../helpers/formatteer.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';
import { VacatureFormulier } from '../vacatures/VacatureFormulier.tsx';
import { BedrijfFormulier } from './BedrijfFormulier.tsx';
import { ContactpersoonFormulier } from './ContactpersoonFormulier.tsx';
import { Vacaturepaginas } from './Vacaturepaginas.tsx';

function Kaart({ titel, actie, children }: { titel: string; actie?: ReactNode | undefined; children: ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={titel} action={actie} slotProps={{ title: { variant: 'h6' } }} sx={{ pb: 0 }} />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Contactpersonen({ bedrijf }: { bedrijf: BedrijfDetails }) {
  const mutaties = useContactpersoonMutaties();
  const { meld, meldFout } = useMeldingen();
  const [formulier, setFormulier] = useState<{ open: boolean; bestaand?: Contactpersoon }>({ open: false });
  const [gesprek, setGesprek] = useState<Contactpersoon | null>(null);
  const [verwijderen, setVerwijderen] = useState<Contactpersoon | null>(null);

  return (
    <Kaart
      titel={`Contactpersonen (${bedrijf.contactpersonen.length})`}
      actie={
        <Button size="small" startIcon={<AddIcon />} onClick={() => setFormulier({ open: true })}>
          Toevoegen
        </Button>
      }
    >
      {bedrijf.contactpersonen.length === 0 ? (
        <Typography color="text.secondary">Nog geen contactpersonen.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Naam</TableCell>
              <TableCell>Functie</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefoon</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {bedrijf.contactpersonen.map((contactpersoon) => (
              <TableRow key={contactpersoon.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>
                  {volledigeNaam(contactpersoon)}
                  {contactpersoon.linkedinUrl && (
                    <>
                      {' '}
                      <Link href={contactpersoon.linkedinUrl} target="_blank" rel="noreferrer" variant="caption">
                        LinkedIn
                      </Link>
                    </>
                  )}
                </TableCell>
                <TableCell>{ofStreepje(contactpersoon.functie)}</TableCell>
                <TableCell>
                  {contactpersoon.email ? (
                    <Link href={`mailto:${contactpersoon.email}`}>{contactpersoon.email}</Link>
                  ) : (
                    '–'
                  )}
                </TableCell>
                <TableCell>
                  {contactpersoon.telefoon ? (
                    <Link href={`tel:${contactpersoon.telefoon}`}>{contactpersoon.telefoon}</Link>
                  ) : (
                    '–'
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Gesprek vastleggen">
                    <IconButton size="small" aria-label="Gesprek vastleggen" onClick={() => setGesprek(contactpersoon)}>
                      <ForumIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    aria-label="Bewerken"
                    onClick={() => setFormulier({ open: true, bestaand: contactpersoon })}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="Verwijderen" onClick={() => setVerwijderen(contactpersoon)}>
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <ContactpersoonFormulier
        open={formulier.open}
        onSluit={() => setFormulier({ open: false })}
        bedrijfId={bedrijf.id}
        bestaand={formulier.bestaand}
      />
      <ContactmomentDialoog
        open={gesprek !== null}
        onSluit={() => setGesprek(null)}
        contactpersoon={gesprek ?? undefined}
      />
      <BevestigDialoog
        open={verwijderen !== null}
        titel="Contactpersoon verwijderen"
        bevestigLabel="Verwijderen"
        gevaarlijk
        bezig={mutaties.verwijder.isPending}
        onSluit={() => setVerwijderen(null)}
        onBevestig={() => {
          if (verwijderen) {
            mutaties.verwijder
              .mutateAsync(verwijderen.id)
              .then(() => {
                meld('Contactpersoon verwijderd');
                setVerwijderen(null);
              })
              .catch(meldFout);
          }
        }}
      >
        {verwijderen ? volledigeNaam(verwijderen) : ''} en de gesprekken met deze persoon worden verwijderd. Dit kan
        niet zolang de persoon aanspreekpunt is van een vacature.
      </BevestigDialoog>
    </Kaart>
  );
}

function Vacatures({ bedrijf }: { bedrijf: BedrijfDetails }) {
  const vacatures = useVacatures({ bedrijfId: bedrijf.id, limiet: 50 });
  const [nieuw, setNieuw] = useState(false);
  const navigeer = useNavigate();
  return (
    <Kaart
      titel="Vacatures"
      actie={
        <Button size="small" startIcon={<AddIcon />} onClick={() => setNieuw(true)}>
          Nieuwe vacature
        </Button>
      }
    >
      <Laadstatus query={vacatures} leeg="Nog geen vacatures van dit bedrijf.">
        {(lijst) => (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Titel</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gewijzigd</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lijst.map((vacature) => (
                <TableRow key={vacature.id} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/vacatures/${vacature.id}`}
                      underline="hover"
                      sx={{ fontWeight: 500 }}
                    >
                      {vacature.titel}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusChip soort="vacature" waarde={vacature.status} />
                  </TableCell>
                  <TableCell>{datum(vacature.gewijzigdOp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Laadstatus>
      <VacatureFormulier
        open={nieuw}
        onSluit={() => setNieuw(false)}
        standaardBedrijf={bedrijf}
        onOpgeslagen={(vacature) => void navigeer(`/vacatures/${vacature.id}`)}
      />
    </Kaart>
  );
}

function Gesprekken({ bedrijf }: { bedrijf: BedrijfDetails }) {
  const { offset, limiet, zetOffset } = useZoekparameters(25);
  const gesprekken = useContactmomentenVan('bedrijf', bedrijf.id, { limiet, offset });
  const [gesprek, setGesprek] = useState(false);
  return (
    <Kaart
      titel="Gesprekken"
      actie={
        <Button size="small" startIcon={<AddIcon />} onClick={() => setGesprek(true)}>
          Gesprek vastleggen
        </Button>
      }
    >
      <ContactmomentenLijst
        query={gesprekken}
        toon={{ bedrijf: false }}
        paginering={{ offset, limiet, onOffset: zetOffset }}
      />
      <ContactmomentDialoog open={gesprek} onSluit={() => setGesprek(false)} bedrijfId={bedrijf.id} />
    </Kaart>
  );
}

/**
 * De pagina van een bedrijf: gegevens, contactpersonen, vacatures en gesprekken.
 * @returns De pagina.
 */
export function BedrijfPagina() {
  const { id = '' } = useParams();
  const bedrijf = useBedrijf(id);
  const { isBeheerder } = useSessie();
  const mutaties = useBedrijfMutaties();
  const { meld, meldFout } = useMeldingen();
  const navigeer = useNavigate();
  const [bewerken, setBewerken] = useState(false);
  const [verwijderen, setVerwijderen] = useState(false);

  return (
    <Laadstatus query={bedrijf}>
      {(details) => (
        <Pagina
          titel={details.naam}
          terug={{ naar: '/bedrijven', label: 'Bedrijven' }}
          ondertitel={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
              <StatusChip soort="relatie" waarde={details.relatie} />
              {details.locatie && <span>{locatieLabel(details.locatie)}</span>}
            </Stack>
          }
          acties={
            <>
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setBewerken(true)}>
                Bewerken
              </Button>
              {isBeheerder && (
                <Button color="error" startIcon={<DeleteOutlinedIcon />} onClick={() => setVerwijderen(true)}>
                  Verwijderen
                </Button>
              )}
            </>
          }
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Kaart titel="Gegevens">
                <Kenmerken
                  items={[
                    { label: 'KvK-nummer', waarde: ofStreepje(details.kvkNummer) },
                    { label: 'Sector', waarde: ofStreepje(details.sector) },
                    {
                      label: 'Website',
                      waarde: details.website ? (
                        <Link href={details.website} target="_blank" rel="noreferrer">
                          {details.website.replace(/^https?:\/\//, '')}
                        </Link>
                      ) : null,
                    },
                    {
                      label: 'Telefoon',
                      waarde: details.telefoon ? (
                        <Link href={`tel:${details.telefoon}`}>{details.telefoon}</Link>
                      ) : null,
                    },
                    { label: 'Vestigingsplaats', waarde: details.locatie ? locatieLabel(details.locatie) : null },
                    { label: 'Aangemaakt', waarde: datum(details.aangemaaktOp) },
                  ]}
                />
              </Kaart>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Contactpersonen bedrijf={details} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Vacatures bedrijf={details} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Gesprekken bedrijf={details} />
            </Grid>
            <Grid size={12}>
              <Vacaturepaginas bedrijf={details} />
            </Grid>
          </Grid>
          <BedrijfFormulier open={bewerken} onSluit={() => setBewerken(false)} bestaand={details} />
          <BevestigDialoog
            open={verwijderen}
            titel="Bedrijf verwijderen"
            bevestigLabel="Verwijderen"
            gevaarlijk
            bezig={mutaties.verwijder.isPending}
            onSluit={() => setVerwijderen(false)}
            onBevestig={() =>
              mutaties.verwijder
                .mutateAsync(details.id)
                .then(() => {
                  meld('Bedrijf verwijderd');
                  void navigeer('/bedrijven');
                })
                .catch(meldFout)
            }
          >
            {details.naam} wordt verwijderd met zijn contactpersonen. Dit kan niet zolang er vacatures of gesprekken aan
            het bedrijf hangen.
          </BevestigDialoog>
        </Pagina>
      )}
    </Laadstatus>
  );
}
